import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { adminDataApi, UserDto, UserInput } from "../../services/admin-data-api";

const USER_ROLES = [
  { value: "ADMIN", label: "Admin", color: "error" as const },
  { value: "CASE_MANAGER", label: "Case Manager", color: "primary" as const },
  { value: "TREASURY_OFFICER", label: "Treasury Officer", color: "info" as const },
  { value: "AUTHORIZING_OFFICER", label: "Authorizing Officer", color: "secondary" as const },
  { value: "AUDITOR", label: "Auditor", color: "warning" as const },
  { value: "FIELD_OFFICER", label: "Field Officer", color: "success" as const },
  { value: "CITIZEN", label: "Citizen", color: "default" as const },
];

export function UsersPage() {
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDto | null>(null);
  const [userForm, setUserForm] = useState<UserInput>({
    email: "",
    password: "",
    role: "CASE_MANAGER",
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminDataApi.getUsers();
      setUsers(data);
    } catch (err: any) {
      console.error("Failed to load users:", err);
      setError(err.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setEditingUser(null);
    setUserForm({
      email: "",
      password: "",
      role: "CASE_MANAGER",
    });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (user: UserDto) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      password: "", // Don't populate password for security
      role: user.role,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setUserForm({
      email: "",
      password: "",
      role: "CASE_MANAGER",
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        // Update existing user - only send changed fields
        const updatePayload: Partial<UserInput> = {
          email: userForm.email !== editingUser.email ? userForm.email : undefined,
          role: userForm.role !== editingUser.role ? userForm.role : undefined,
        };
        // Only include password if it was provided
        if (userForm.password.trim()) {
          updatePayload.password = userForm.password;
        }
        // Remove undefined fields
        Object.keys(updatePayload).forEach((key) => {
          if (updatePayload[key as keyof UserInput] === undefined) {
            delete updatePayload[key as keyof UserInput];
          }
        });

        await adminDataApi.updateUser(editingUser.id, updatePayload);
      } else {
        // Create new user - all fields required
        if (!userForm.email || !userForm.password || !userForm.role) {
          setError("Email, password, and role are required");
          return;
        }
        await adminDataApi.createUser(userForm);
      }
      handleCloseDialog();
      loadUsers();
    } catch (err: any) {
      console.error("Failed to save user:", err);
      setError(err.response?.data?.message || "Failed to save user");
    }
  };

  const handleDelete = async (id: string, email: string) => {
    if (!confirm(`Are you sure you want to delete user "${email}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await adminDataApi.deleteUser(id);
      loadUsers();
    } catch (err: any) {
      console.error("Failed to delete user:", err);
      setError(err.response?.data?.message || "Failed to delete user");
    }
  };

  const getRoleDisplay = (role: string) => {
    const roleConfig = USER_ROLES.find((r) => r.value === role);
    return roleConfig || { value: role, label: role, color: "default" as const };
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage staff accounts and permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          New User
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => {
                    const roleDisplay = getRoleDisplay(user.role);
                    return (
                      <TableRow key={user.id} hover>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={roleDisplay.label}
                            color={roleDisplay.color}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(user)}
                            title="Edit user"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(user.id, user.email)}
                            title="Delete user"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? "Edit User" : "Create New User"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={editingUser ? "Password (leave blank to keep current)" : "Password"}
              type="password"
              value={userForm.password}
              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              fullWidth
              required={!editingUser}
              helperText={editingUser ? "Only enter a password if you want to change it" : ""}
            />
            <TextField
              select
              label="Role"
              value={userForm.role}
              onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              fullWidth
              required
            >
              {USER_ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingUser ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
